'use client';

import { Maybe } from 'graphql/jsutils/Maybe';
import React, { useMemo } from 'react';
import {
  DistinctFacetFragment,
  RangeFacetFragment,
} from '@business/gql/graphql';
import Button from '../button/buttonUI';
import Icon from '../icon/icon';
import useFacetsSort, { useFacetPrice } from '@utils/hooks/sort/useFacets';
import { formatPrice } from '@utils/currency/formatPrice';
import useShallowRouting from '@utils/hooks/sort/useShallowSearchParams';
import { useMounted } from '@utils/hooks/useMounted';

type ActiveFiltersProps = {
  facets: Maybe<DistinctFacetFragment | RangeFacetFragment>[] | undefined;
  locale: string;
};

const ActiveFilters = ({ facets, locale }: ActiveFiltersProps) => {
  const setFacet = useFacetsSort();
  const setFacetPrice = useFacetPrice();
  const { shallowSearchParams } = useShallowRouting();
  const mounted = useMounted();

  const shallowCategoryParam = shallowSearchParams?.get('category');
  const shallowBrandParam = shallowSearchParams?.get('brand');
  const shallowDesignerParam = shallowSearchParams?.get('designer');

  const activeCategoryFilters = useMemo(() => {
    return facets
      ?.filter((facet) => facet?.name === 'Category')
      .flatMap((facet) => (facet as DistinctFacetFragment)?.items)
      .filter((item) =>
        item?.item && shallowCategoryParam
          ? shallowCategoryParam?.split(',').includes(item.item)
          : false,
      );
  }, [facets, shallowCategoryParam]);

  const activeBrandFilters = useMemo(() => {
    return facets
      ?.filter((facet) => facet?.name === 'Brand')
      .flatMap((facet) => (facet as DistinctFacetFragment)?.items)
      .filter((item) =>
        item?.item && shallowBrandParam
          ? shallowBrandParam?.split(',').includes(item.item)
          : false,
      );
  }, [facets, shallowBrandParam]);

  const activeDesignerFilters = useMemo(() => {
    return facets
      ?.filter((facet) => facet?.name === 'Designer')
      .flatMap((facet) => (facet as DistinctFacetFragment)?.items)
      .filter((item) =>
        item?.item && shallowDesignerParam
          ? shallowDesignerParam?.split(',').includes(item.item)
          : false,
      );
  }, [facets, shallowDesignerParam]);

  const activePriceFilter = useMemo(() => {
    return facets?.find((facet) => facet?.name === 'Price');
  }, [facets]);

  if (
    (!activeCategoryFilters?.length &&
      !activeBrandFilters?.length &&
      !activeDesignerFilters?.length &&
      !(activePriceFilter as RangeFacetFragment)?.selectedMin &&
      !(activePriceFilter as RangeFacetFragment)?.selectedMax) ||
    !mounted
  ) {
    return null;
  }

  return (
    <div className="mb-4 hidden w-full flex-wrap gap-2 md:flex">
      {(activePriceFilter as RangeFacetFragment)?.selectedMin &&
      (activePriceFilter as RangeFacetFragment)?.selectedMax ? (
        <Button
          onClick={() => {
            setFacetPrice({ type: 'range', min: null, max: null });
          }}
          className="border-borderColor flex bg-beige p-2 text-sm">
          {formatPrice(
            (activePriceFilter as RangeFacetFragment)?.selectedMin,
            'whole',
          )}
          &nbsp;-&nbsp;
          {formatPrice(
            (activePriceFilter as RangeFacetFragment)?.selectedMax,
            'whole',
          )}
          <Icon name="cross" size={16} />
        </Button>
      ) : null}
      {activeCategoryFilters?.length
        ? activeCategoryFilters?.map((item, idx) => (
            <Button
              onClick={() => {
                setFacet({ id: 'category', name: item?.item ?? '' });
              }}
              key={item ? item?.item : '' + idx}
              className="border-borderColor flex bg-beige p-2 text-sm">
              {item?.name}
              <Icon name="cross" size={16} />
            </Button>
          ))
        : null}
      {activeBrandFilters?.length
        ? activeBrandFilters?.map((item, idx) => (
            <Button
              onClick={() => {
                setFacet({ id: 'brand', name: item?.item ?? '' });
              }}
              key={item ? item?.item : '' + idx}
              className="border-borderColor flex bg-beige p-2 text-sm">
              {item?.name}
              <Icon name="cross" size={16} />
            </Button>
          ))
        : null}
      {activeDesignerFilters?.length
        ? activeDesignerFilters?.map((item, idx) => (
            <Button
              onClick={() => {
                setFacet({ id: 'designer', name: item?.item ?? '' });
              }}
              key={item ? item?.item : '' + idx}
              className="border-borderColor flex bg-beige p-2 text-sm">
              {item?.name}
              <Icon name="cross" size={16} />
            </Button>
          ))
        : null}
    </div>
  );
};

export default ActiveFilters;
